import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as LinkIcon, RefreshCw, Send, ShieldOff, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import AdminAccessPanel from "@/admin/AdminAccessPanel";

import { useAdminPermissions } from "@/admin/PermissionsContext";
import ConfirmDialog from "@/design-system/components/ConfirmDialog";
import { api, formatApiError, classifyApiError } from "@/api/client";

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Admin",
  manager: "Manager",
  support: "Support",
  dispute_specialist: "Dispute specialist",
  funding_specialist: "Funding specialist",
};

// Role options for the invite modal. Owner can additionally invite "admin";
// non-owners are restricted to the four operational roles.
const INVITABLE_ROLES_OWNER = ["admin", "manager", "support", "dispute_specialist", "funding_specialist"];
const INVITABLE_ROLES_NON_OWNER = ["manager", "support", "dispute_specialist", "funding_specialist"];

function StatusPill({ status }) {
  const map = {
    pending: { label: "Pending", bg: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)", border: "var(--ds-color-accent-soft)" },
    accepted: { label: "Accepted", bg: "rgba(74,222,128,0.12)", color: "var(--ds-color-success)", border: "rgba(74,222,128,0.35)" },
    expired: { label: "Expired", bg: "rgba(202,138,4,0.15)", color: "var(--ds-color-warning)", border: "rgba(202,138,4,0.4)" },
    revoked: { label: "Revoked", bg: "var(--ds-color-danger-soft)", color: "var(--ds-color-danger)", border: "var(--ds-color-danger)" },
  };
  const s = map[status] || { label: status, bg: "var(--ds-color-admin-surface-muted)", color: "var(--ds-color-ink-muted)", border: "var(--ds-color-admin-border)" };
  return (
    <span
      data-testid={`invite-status-${status}`}
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em]"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}
    >
      {s.label}
    </span>
  );
}

function EmailDeliveryPill({ value }) {
  if (!value) return null;
  const map = {
    sandbox: { label: "Sandbox", color: "var(--ds-color-accent)" },
    sent: { label: "Sent", color: "var(--ds-color-success)" },
    pending: { label: "Sending…", color: "var(--ds-color-warning)" },
    failed: { label: "Failed", color: "var(--ds-color-danger)" },
    bounced: { label: "Bounced", color: "var(--ds-color-danger)" },
    spam_complaint: { label: "Spam", color: "var(--ds-color-danger)" },
  };
  const s = map[value] || { label: value, color: "var(--ds-color-ink-muted)" };
  return (
    <span
      data-testid={`invite-email-${value}`}
      className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em]"
      style={{ color: s.color, background: "var(--ds-color-admin-surface-muted)" }}
    >
      {s.label}
    </span>
  );
}

function InviteModal({ open, onClose, onCreated, isOwner }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("support");
  const [busy, setBusy] = useState(false);
  const [createdInvite, setCreatedInvite] = useState(null);

  const roles = isOwner ? INVITABLE_ROLES_OWNER : INVITABLE_ROLES_NON_OWNER;

  useEffect(() => {
    if (open) {
      setEmail("");
      setRole(roles[0]);
      setCreatedInvite(null);
    }
  }, [open, roles]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/team/invitations", { email: email.trim().toLowerCase(), team_role: role });
      setCreatedInvite(data);
      toast.success("Invitation sent");
      onCreated && onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!createdInvite?.copy_link_url) return;
    try {
      await navigator.clipboard.writeText(createdInvite.copy_link_url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy link");
    }
  }

  return (
    <div
      data-testid="invite-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.65)" }}
      onClick={onClose}
    >
      <div
        data-testid="invite-modal"
        className="w-full max-w-md rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] p-6"
        style={{ background: "var(--ds-color-surface)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-accent)]">Team access</div>
            <h2 className="font-display text-xl text-[var(--ds-color-ink)] mt-1">Invite a teammate</h2>
          </div>
          <button data-testid="invite-modal-close" onClick={onClose} className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!createdInvite ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1.5">Email address</label>
              <input
                data-testid="invite-input-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@example.com"
                className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1.5">Role</label>
              <select
                data-testid="invite-input-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
                style={{ background: "var(--ds-color-surface)" }}
              >
                {roles.map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>
              <p className="text-[11px] text-[var(--ds-color-ink-muted)] mt-2 leading-relaxed">
                Invitee will receive an email with a 72-hour link to set their password. Owner-only fields (role changes,
                member deletion) remain protected regardless of role.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                data-testid="invite-modal-cancel"
                onClick={onClose}
                className="rounded-md border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-sm text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="invite-modal-submit"
                className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-medium disabled:opacity-60"
                style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)" }}
              >
                <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send invitation"}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4" data-testid="invite-modal-success">
            <div className="rounded-md border border-[rgba(74,222,128,0.3)] bg-[rgba(74,222,128,0.06)] p-3 text-sm text-[var(--ds-color-success)]">
              Invitation sent to <strong className="text-[var(--ds-color-ink)]">{createdInvite.email}</strong>. They&apos;ll receive a Postmark email shortly.
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1.5">Or share this link manually</label>
              <div className="flex gap-2">
                <input
                  readOnly
                  data-testid="invite-modal-accept-url"
                  value={createdInvite.copy_link_url}
                  className="flex-1 rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-xs text-[var(--ds-color-ink)]"
                />
                <button
                  type="button"
                  data-testid="invite-modal-copy-link"
                  onClick={copyLink}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[var(--ds-color-admin-border)] px-3 py-2 text-xs text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-admin-surface-muted)]"
                >
                  <LinkIcon className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                data-testid="invite-modal-done"
                onClick={onClose}
                className="rounded-md px-3.5 py-1.5 text-sm font-medium"
                style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)" }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminTeam() {
  const { isOwner, has, teamRole } = useAdminPermissions();
  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(null); // null | invite id
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(null); // null | member
  const [removeBusy, setRemoveBusy] = useState(false);

  const [loadError, setLoadError] = useState(null);

  const canManage = has("manage_team") || isOwner;

  const reloadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [mRes, iRes] = await Promise.all([
        api.get("/admin/team/members"),
        api.get("/admin/team/invitations?limit=200"),
      ]);
      setMembers(mRes.data?.items || []);
      setInvites(iRes.data?.items || []);
    } catch (e) {
      // Distinguish 404 (endpoint not wired) from other errors so the UI
      // renders the appropriate empty-state instead of a scary red toast.
      const info = classifyApiError(e);
      setLoadError(info);
      if (info.kind !== "not_available") {
        toast.error(info.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage) reloadAll();
    else setLoading(false);
  }, [canManage, reloadAll]);

  async function handleResend(id) {
    try {
      await api.post(`/admin/team/invitations/${id}/resend`);
      toast.success("Invitation resent");
      reloadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  async function handleRevoke(id) {
    setRevokeBusy(true);
    try {
      await api.delete(`/admin/team/invitations/${id}`);
      toast.success("Invitation revoked");
      setConfirmingRevoke(null);
      reloadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRevokeBusy(false);
    }
  }
  async function handleCopy(url) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy link");
    }
  }
  async function handleSuspendToggle(member) {
    const verb = member.is_suspended ? "activate" : "deactivate";
    try {
      await api.patch(`/admin/team/members/${member.id}/${verb}`);
      toast.success(`Member ${verb}d`);
      reloadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  async function handleRoleChange(member, newRole) {
    try {
      await api.patch(`/admin/team/members/${member.id}/role`, { team_role: newRole });
      toast.success("Role updated");
      reloadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  async function handleDelete(member) {
    try {
      await api.delete(`/admin/team/members/${member.id}`);
      toast.success("Team member removed");
      reloadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const memberStats = useMemo(() => {
    const total = members.length;
    const suspended = members.filter((m) => m.is_suspended).length;
    return { total, active: total - suspended, suspended };
  }, [members]);
  const inviteStats = useMemo(() => {
    const pending = invites.filter((i) => i.status === "pending").length;
    return { total: invites.length, pending };
  }, [invites]);

  if (!canManage) {
    return (
      <AdminLayout title="Team" subtitle="Manage operations team access">
        <div className="mb-4 max-w-3xl">
          <AdminPageHelp
            title="Team"
            purpose="Manage the operations team who has admin access — invite, revoke, and audit team members."
            configures="Invite new team members, revoke pending invitations, remove active members, view invitation history."
            impact="Revoked members lose access on next request. Removed members are signed out on next request."
            safety="Every action is logged. Removed members can be re-invited later; team invitations are single-use and time-limited."
          />
        </div>
        <AdminAccessPanel
          testId="team-no-access"
          kind="forbidden"
          heading="Your role can't manage the team."
          body="Team management is limited to workspace owners and roles with the manage_team permission. Ask a workspace owner to grant access, or switch to an account with the required role."
          detail={`Current role: ${teamRole || "(none)"}`}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Team"
      subtitle="Operations staff with admin-console access"
      actions={
        <button
          type="button"
          data-testid="team-open-invite-modal"
          onClick={() => setInviteOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-medium"
          style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)" }}
        >
          <UserPlus className="w-3.5 h-3.5" /> Invite teammate
        </button>
      }
    >
      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} onCreated={reloadAll} isOwner={isOwner} />

      {/* Tabs */}
      <div className="flex items-center border-b border-[var(--ds-color-admin-border)] mb-6">
        <button
          type="button"
          data-testid="team-tab-members"
          onClick={() => setTab("members")}
          className={`px-4 py-2.5 text-sm transition-colors ${tab === "members" ? "text-[var(--ds-color-ink)]" : "text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink-muted)]"}`}
          style={{ borderBottom: tab === "members" ? "2px solid var(--ds-color-accent)" : "2px solid transparent" }}
        >
          Members <span className="ml-1 text-[10px] text-[var(--ds-color-ink-muted)]">({memberStats.total})</span>
        </button>
        <button
          type="button"
          data-testid="team-tab-invitations"
          onClick={() => setTab("invitations")}
          className={`px-4 py-2.5 text-sm transition-colors ${tab === "invitations" ? "text-[var(--ds-color-ink)]" : "text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink-muted)]"}`}
          style={{ borderBottom: tab === "invitations" ? "2px solid var(--ds-color-accent)" : "2px solid transparent" }}
        >
          Invitations <span className="ml-1 text-[10px] text-[var(--ds-color-ink-muted)]">({inviteStats.total})</span>
        </button>
      </div>

      {loading ? (
        <div data-testid="team-loading" className="text-sm text-[var(--ds-color-ink-muted)]">Loading…</div>
      ) : loadError && loadError.kind === "not_available" ? (
        <AdminAccessPanel
          testId="team-not-available"
          kind="not_available"
          detail={loadError.message}
        />
      ) : loadError && loadError.kind === "server_error" ? (
        <AdminAccessPanel
          testId="team-server-error"
          kind="server_error"
          detail={loadError.message}
        />
      ) : tab === "members" ? (
        <MembersTable
          members={members}
          isOwner={isOwner}
          onRoleChange={handleRoleChange}
          onSuspendToggle={handleSuspendToggle}
          onDelete={(m) => setConfirmingRemove(m)}
        />
      ) : (
        <InvitationsTable
          invites={invites}
          onResend={handleResend}
          onRevoke={(id) => setConfirmingRevoke(id)}
          onCopy={handleCopy}
        />
      )}

      <ConfirmDialog
        open={!!confirmingRevoke}
        onCancel={() => setConfirmingRevoke(null)}
        onConfirm={() => confirmingRevoke && handleRevoke(confirmingRevoke)}
        busy={revokeBusy}
        variant="destructive"
        title="Revoke this invitation?"
        description="The invite link will stop working immediately. If the recipient still needs access, send a fresh invitation."
        confirmLabel="Revoke invitation"
        testId="team-revoke-confirm"
      />

      <ConfirmDialog
        open={!!confirmingRemove}
        onCancel={() => setConfirmingRemove(null)}
        onConfirm={() => confirmingRemove && handleDelete(confirmingRemove)}
        busy={removeBusy}
        variant="destructive"
        title={confirmingRemove ? `Remove ${confirmingRemove.email} from the team?` : "Remove team member"}
        description="This deletes their account and revokes access immediately. Their audit history remains, but they will no longer be able to sign in."
        confirmLabel="Remove team member"
        testId="team-remove-confirm"
      />
    </AdminLayout>
  );
}

function MembersTable({ members, isOwner, onRoleChange, onSuspendToggle, onDelete }) {
  if (!members.length) {
    return (
      <div data-testid="team-members-empty" className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] p-8 text-center text-sm text-[var(--ds-color-ink-muted)]">
        No team members yet. Send your first invitation above.
      </div>
    );
  }
  return (
    <div data-testid="team-members-table" className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] overflow-hidden" style={{ background: "var(--ds-color-surface)" }}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">
            <th className="px-4 py-3 font-medium">Member</th>
            <th className="px-4 py-3 font-medium">Role</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Joined</th>
            <th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const isOwnerRow = m.team_role === "owner";
            return (
              <tr key={m.id} data-testid={`team-member-row-${m.id}`} className="border-t border-[var(--ds-color-admin-border)]">
                <td className="px-4 py-3">
                  <div className="text-[var(--ds-color-ink)]">{[m.first_name, m.last_name].filter(Boolean).join(" ") || m.email}</div>
                  <div className="text-[11px] text-[var(--ds-color-ink-muted)]">{m.email}</div>
                </td>
                <td className="px-4 py-3">
                  {isOwner && !isOwnerRow ? (
                    <select
                      data-testid={`team-member-role-select-${m.id}`}
                      value={m.team_role || ""}
                      onChange={(e) => onRoleChange(m, e.target.value)}
                      className="rounded border border-[var(--ds-color-admin-border)] bg-transparent px-2 py-1 text-xs text-[var(--ds-color-ink)]"
                      style={{ background: "var(--ds-color-surface)" }}
                    >
                      {Object.entries(ROLE_LABELS)
                        .filter(([k]) => k !== "owner")
                        .map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                  ) : (
                    <span className="text-[var(--ds-color-ink-muted)]">{ROLE_LABELS[m.team_role] || "—"}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {m.is_suspended ? (
                    <span className="text-[var(--ds-color-danger)] text-xs">Suspended</span>
                  ) : (
                    <span className="text-[var(--ds-color-success)] text-xs">Active</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[var(--ds-color-ink-muted)] text-xs">
                  {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {!isOwnerRow && (
                      <button
                        type="button"
                        data-testid={`team-member-suspend-${m.id}`}
                        onClick={() => onSuspendToggle(m)}
                        className="inline-flex items-center gap-1 rounded border border-[var(--ds-color-admin-border)] px-2 py-1 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
                      >
                        <ShieldOff className="w-3 h-3" /> {m.is_suspended ? "Activate" : "Suspend"}
                      </button>
                    )}
                    {isOwner && !isOwnerRow && (
                      <button
                        type="button"
                        data-testid={`team-member-delete-${m.id}`}
                        onClick={() => onDelete(m)}
                        className="inline-flex items-center gap-1 rounded border border-[var(--ds-color-danger)] px-2 py-1 text-xs text-[var(--ds-color-danger)] hover:bg-[var(--ds-color-danger-soft)]"
                      >
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function InvitationsTable({ invites, onResend, onRevoke, onCopy }) {
  if (!invites.length) {
    return (
      <div data-testid="team-invites-empty" className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] p-8 text-center text-sm text-[var(--ds-color-ink-muted)]">
        No invitations yet.
      </div>
    );
  }
  return (
    <div data-testid="team-invitations-table" className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] overflow-hidden" style={{ background: "var(--ds-color-surface)" }}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">
            <th className="px-4 py-3 font-medium">Email</th>
            <th className="px-4 py-3 font-medium">Role</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Email delivery</th>
            <th className="px-4 py-3 font-medium">Created</th>
            <th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {invites.map((inv) => (
            <tr key={inv.id} data-testid={`team-invite-row-${inv.id}`} className="border-t border-[var(--ds-color-admin-border)]">
              <td className="px-4 py-3 text-[var(--ds-color-ink)]">{inv.email}</td>
              <td className="px-4 py-3 text-[var(--ds-color-ink-muted)]">{ROLE_LABELS[inv.team_role] || inv.team_role}</td>
              <td className="px-4 py-3"><StatusPill status={inv.status} /></td>
              <td className="px-4 py-3"><EmailDeliveryPill value={inv.email_send_status} /></td>
              <td className="px-4 py-3 text-[var(--ds-color-ink-muted)] text-xs">
                {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  {inv.status === "pending" && (
                    <>
                      <button
                        type="button"
                        data-testid={`team-invite-copy-${inv.id}`}
                        onClick={() => onCopy(inv.copy_link_url)}
                        className="inline-flex items-center gap-1 rounded border border-[var(--ds-color-admin-border)] px-2 py-1 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
                      >
                        <LinkIcon className="w-3 h-3" /> Copy link
                      </button>
                      <button
                        type="button"
                        data-testid={`team-invite-resend-${inv.id}`}
                        onClick={() => onResend(inv.id)}
                        className="inline-flex items-center gap-1 rounded border border-[var(--ds-color-admin-border)] px-2 py-1 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
                      >
                        <RefreshCw className="w-3 h-3" /> Resend
                      </button>
                      <button
                        type="button"
                        data-testid={`team-invite-revoke-${inv.id}`}
                        onClick={() => onRevoke(inv.id)}
                        className="inline-flex items-center gap-1 rounded border border-[var(--ds-color-danger)] px-2 py-1 text-xs text-[var(--ds-color-danger)] hover:bg-[var(--ds-color-danger-soft)]"
                      >
                        <Trash2 className="w-3 h-3" /> Revoke
                      </button>
                    </>
                  )}
                  {inv.status === "expired" && (
                    <button
                      type="button"
                      data-testid={`team-invite-resend-${inv.id}`}
                      onClick={() => onResend(inv.id)}
                      className="inline-flex items-center gap-1 rounded border border-[var(--ds-color-admin-border)] px-2 py-1 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
                    >
                      <RefreshCw className="w-3 h-3" /> Resend
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
